import { assign, createMachine, sendParent, sendTo, spawn } from 'xstate';
import { PAGE_FUNCTION, SUB_FUNCTION } from '../../const/analytics';
import { PAYOFF_MACHINE_STATES_NAMES } from '../../const/machines';
import { formMachine, getValues } from '../../xform';
import paymentMachine from '../payment/paymentMachine';
import { savePaymentResult, submitFailureAnalyticsHandler } from './helpers';
import paymentAmountConfig from './paymentAmountConfig';
import paymentDateConfig from './paymentDateConfig';
import paymentMethodConfig from './paymentMethodConfig';
export default createMachine({
    predictableActionArguments: true,
    id: 'payoffMachine',
    initial: PAYOFF_MACHINE_STATES_NAMES.INITIAL,
    schema: {
        events: {}
    },
    context: {
        apiMachineRef: undefined,
        errorType: undefined,
        featureFlags: undefined,
        form: undefined,
        hasPaymentMethodBusinessValidationError: false,
        triggerAnalyticsEvent: () => undefined
    },
    states: {
        edit: {
            entry: [
                sendParent({ type: 'HOE_UPDATE_BANNER_VISIBILITY' }),
                assign({
                    form({ apiMachineRef, form }) {
                        if (form) {
                            return form;
                        }
                        return spawn(formMachine.withContext({
                            fieldConfigs: [
                                paymentAmountConfig(apiMachineRef),
                                paymentDateConfig(apiMachineRef),
                                paymentMethodConfig(apiMachineRef)
                            ]
                        }), {
                            name: 'payoffPaymentForm'
                        });
                    }
                })
            ],
            on: {
                SET_NEW_PAYMENT_METHOD: {
                    actions: [
                        sendTo(({ apiMachineRef }) => apiMachineRef, {
                            type: 'FETCH_PAYMENT_METHODS'
                        }),
                        'sendToPaymentMethodField'
                    ]
                },
                NEXT: {
                    target: PAYOFF_MACHINE_STATES_NAMES.REVIEW,
                    cond: 'isFormValid'
                }
            }
        },
        review: {
            entry: sendParent({ type: 'HOE_UPDATE_BANNER_VISIBILITY' }),
            on: {
                EDIT: {
                    target: PAYOFF_MACHINE_STATES_NAMES.EDIT,
                    actions: {
                        type: 'triggerAnalyticsEvent',
                        eventOptions: (context) => {
                            const { form } = context;
                            const payOffPaymentForm = getValues(form.getSnapshot().context.actors);
                            return {
                                message: payOffPaymentForm.paymentOption,
                                pageFunction: PAGE_FUNCTION.MAKE_PAYMENT.PAYMENTS,
                                pageName: 'edit',
                                pageSubFunction: SUB_FUNCTION.MAKE_PAYMENT.PAYMENT
                            };
                        }
                    }
                },
                SUBMIT: {
                    target: PAYOFF_MACHINE_STATES_NAMES.SUBMITTING
                }
            }
        },
        submitting: {
            entry: sendParent({ type: 'HOE_UPDATE_BANNER_VISIBILITY' }),
            invoke: {
                id: 'payment',
                src: paymentMachine,
                data: ({ apiMachineRef, featureFlags, form }) => {
                    const context = {
                        apiMachineRef,
                        featureFlags,
                        payoffPaymentFormMachineRef: form
                    };
                    return context;
                },
                onDone: {
                    target: PAYOFF_MACHINE_STATES_NAMES.SUCCESS,
                    actions: ['savePaymentResult']
                },
                onError: {
                    target: PAYOFF_MACHINE_STATES_NAMES.ERROR,
                    actions: [
                        'savePaymentResult',
                        {
                            type: 'triggerAnalyticsEvent',
                            eventOptions: submitFailureAnalyticsHandler
                        },
                        sendParent({ type: 'HOE_UPDATE_BANNER_VISIBILITY' })
                    ]
                }
            }
        },
        success: {
            entry: [
                sendParent({ type: 'INCREMENT_CONVERSION' }),
                sendParent({ type: 'HOE_UPDATE_BANNER_VISIBILITY' })
            ]
        },
        error: {},
        cancelled: {
            type: 'final',
            entry: sendParent({ type: 'CANCEL' })
        }
    },
    on: {
        CANCEL: {
            target: 'cancelled',
            actions: {
                type: 'triggerAnalyticsEvent',
                eventOptions: () => {
                    return {
                        pageFunction: PAGE_FUNCTION.MAKE_PAYMENT.PAYMENTS,
                        pageName: 'cancel',
                        pageSubFunction: SUB_FUNCTION.MAKE_PAYMENT.PAYMENT
                    };
                }
            }
        }
    }
}, {
    actions: {
        triggerAnalyticsEvent: (context, event, meta) => {
            const { triggerAnalyticsEvent } = context;
            triggerAnalyticsEvent(meta.action.eventOptions(context));
        },
        savePaymentResult,
        updateBusinessValidationError: assign({
            hasPaymentMethodBusinessValidationError: (context, { hasPaymentMethodBusinessValidationError }) => hasPaymentMethodBusinessValidationError
        }),
        sendToPaymentMethodField: sendTo(({ form }) => {
            return form.getSnapshot().context.actors.paymentMethod;
        }, (_, { paymentMethodId }) => {
            return {
                type: 'CHANGE',
                value: paymentMethodId
            };
        })
    },
    guards: {
        isFormValid: ({ form }) => {
            return form.getSnapshot().matches('validation.valid');
        }
    }
});
